<template>
  <!-- Mobile: Direct BetSlip rendering -->
  <div v-if="isMobile && isOpen" class="mobile-betslip-panel">
    <div class="mobile-betslip-header">
      <h3 class="mobile-betslip-title">BetSlip</h3>
      <button class="mobile-close-button" @click="close">×</button>
    </div>
    <div ref="mobileBetslipScrollEl" class="mobile-betslip-content">
      <BetSlip @close="close" />
    </div>
  </div>

  <!-- Desktop: Modal overlay -->
  <transition name="modal-slide-down">
    <div v-if="!isMobile && isOpen" class="desktop-modal-overlay" @click.self="close">
      <div class="desktop-modal-box">
        <div class="desktop-modal-header">
          <h3 class="desktop-modal-title">BetSlip</h3>
          <button class="desktop-close-button" @click="close">×</button>
        </div>
        <div class="desktop-modal-content">
          <BetSlip @close="close" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import BetSlip from '@/components/BetSlip.vue'
import { iframeCommunication } from '@/utils/iframeCommunication';
import { ref, onMounted, onUnmounted, watch, nextTick, defineProps, defineEmits } from 'vue'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits(['close'])

const isMobile = ref(false)
const mobileBetslipScrollEl = ref<HTMLElement | null>(null)

const checkMobile = () => {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches
}

const isInIframe = iframeCommunication.isRunningInIframe()

/** Preserve page scroll when locking body for full-screen mobile panel (standalone only). */
let bodyScrollLockY = 0

const lockBodyScroll = () => {
  if (isInIframe) return
  bodyScrollLockY = window.scrollY || document.documentElement.scrollTop
  document.body.style.overflow = 'hidden'
  document.body.style.position = 'fixed'
  document.body.style.width = '100%'
  document.body.style.top = `-${bodyScrollLockY}px`
}

const unlockBodyScroll = () => {
  if (isInIframe) return
  document.body.style.overflow = ''
  document.body.style.position = ''
  document.body.style.width = ''
  document.body.style.top = ''
  window.scrollTo(0, bodyScrollLockY)
}

// Watch for mobile panel open/close
watch(() => props.isOpen, (isOpen) => {
  if (isOpen && isMobile.value && isInIframe) {
    iframeCommunication.sendScrollToTop()
    window.scrollTo(0, 0)
  }

  if (isMobile.value && isOpen) {
    lockBodyScroll()
    nextTick(() => {
      if (mobileBetslipScrollEl.value) {
        mobileBetslipScrollEl.value.scrollTop = 0
      }
    })
  } else if (isMobile.value && !isOpen) {
    unlockBodyScroll()
  }
})

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  unlockBodyScroll()
})

const close = () => emit('close')
</script>

<style scoped>
/* Mobile slide-up panel styles */
.mobile-betslip-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;
  background-color: var(--bg-primary);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  transform: translateY(0);
  transition: transform 0.3s ease-in-out;
  padding-bottom: env(safe-area-inset-bottom);
}

html:not(.theme-dark) .mobile-betslip-panel {
  background-color: #ffffff;
}

.mobile-betslip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  flex-shrink: 0;
  background-color: var(--header-bar-surface-bg, #2B364C);
  position: sticky;
  top: 0;
  z-index: 1;
  padding-top: calc(16px + env(safe-area-inset-top));
  color: var(--text-on-card-contrast, var(--text-white));
}

.mobile-betslip-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
  color: inherit;
}

.mobile-close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: var(--text-on-card-contrast, var(--text-white));
  cursor: pointer;
  padding: 8px;
  line-height: 1;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.mobile-close-button:hover {
  background-color: rgba(255, 255, 255, 0.14);
}

.mobile-betslip-content {
  flex: 1;
  overflow-y: auto;
  color: var(--text-primary);
  height: 0;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
}

html:not(.theme-dark) .mobile-betslip-content {
  background: #ffffff;
}

html.theme-dark .mobile-betslip-content {
  background: var(--betslip-body-surface-bg, #2C2C2E);
}

/* Desktop modal styles */
.desktop-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--modal-overlay-backdrop, rgba(16, 21, 32, 0.5));
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 8px;
  overflow-y: auto;
}

.desktop-modal-box {
  background-color: var(--bg-card, var(--bg-secondary));
  width: 100%;
  max-width: 1280px;
  padding: 8px;
  position: relative;
  color: var(--text-primary);
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 8px;
  margin: 0 auto;
  box-sizing: border-box;
  border: 1px solid var(--border-color);
  box-shadow: var(--modal-elevated-shadow, 0 16px 48px rgba(16, 21, 32, 0.12));
}

html:not(.theme-dark) .desktop-modal-box {
  background-color: #ffffff;
}

.desktop-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
}

.desktop-modal-title {
  margin: 0;
  font-size: 18px;
  color: var(--accent-color-text-heading, var(--text-primary));
}

.desktop-close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-secondary);
  cursor: pointer;
}

.desktop-modal-content {
  padding: 8px;
}

/* Responsive behavior */
@media (max-width: 768px) {
  .desktop-modal-overlay {
    display: none;
    padding: 0;
  }
  
  .desktop-modal-box {
    padding: 0;
    max-width: 100%;
    margin: 0;
  }
  
  .mobile-betslip-panel {
    height: 100vh;
  }
  
  .mobile-betslip-header {
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .mobile-close-button {
    touch-action: manipulation;
  }
  
  :global(body.iframe-mode) .mobile-betslip-panel {
    height: 100vh !important;
    min-height: 100vh !important;
    max-height: 100vh !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    padding-bottom: 20px;
    z-index: 10001 !important;
  }
}

@media (min-width: 769px) {
  .mobile-betslip-panel {
    display: none;
  }
  
  .desktop-modal-overlay {
    padding: 8px;
  }
  
  .desktop-modal-box {
    padding: 8px;
    max-width: 1280px;
  }
}

/* Slide down animation shared with BaseModal */
.modal-slide-down-enter-active,
.modal-slide-down-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-slide-down-enter-from,
.modal-slide-down-leave-to {
  opacity: 0;
}

.modal-slide-down-enter-from .desktop-modal-box,
.modal-slide-down-leave-to .desktop-modal-box {
  transform: translateY(-50px);
}
</style>
  